import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { isBonusGamesAvailable } from 'src/common/bonusGames'
import { FILTER_GAMES } from 'src/common/dictionary'

export const useUserBonusesStore = defineStore('userBonuses', () => {
  const freeSpins = ref([])
  const freeSpinsRequestReady = ref(false)
  const programsAvailableReady = ref(false)
  const freeBets = ref(null)
  const freeBetsRequestReady = ref(false)
  const programsAvailable = ref([])
  const activeStep = ref(null)
  const activeStepLoaded = ref(false)
  const activeUserProgram = ref(null)
  const conditionsChoices = ref(null)
  const bonusGameIdsList = ref(null)
  const freeSpinEnd = ref(null)
  const currentLiveCashBack = ref(null)

  const getActiveUserProgram = computed(() => {
    const activeStepProgram = activeStep.value?.userProgram
    if (activeStepProgram && activeStepProgram.isActivated !== false) {
      return activeStepProgram
    }

    return programsAvailable.value.find((el) => el.isActivated)
  })
  const getBonusGameIdsList = computed(() => {
    return bonusGameIdsList.value ?? []
  })
  const getProgramsAvailable = computed(() => {
    return programsAvailable.value.filter((el) => !el.isActivated) || []
  })
  const getActiveStep = computed(() => {
    return activeStep.value
  })

  const getConditionStep = computed(() => {
    return activeStep.value?.condition
  })

  const getFreeSpins = computed(() => {
    return freeSpins.value ?? []
  })
  const getFreeBets = computed(() => {
    return freeBets.value
  })
  const getActiveStepLoaded = computed(() => {
    return activeStepLoaded.value
  })
  const showBonusesGames = computed(() => {
    return isBonusGamesAvailable(activeStep.value, getActiveUserProgram.value)
  })

  function hasFreespinsOnGame({ manufacturer, game }) {
    const activeFreespins = getFreeSpins.value.filter((bonus) => bonus.activated)
    if (!activeFreespins.length) {
      return false
    }

    return !!activeFreespins.find((bonus) => {
      return manufacturer === bonus.game.manufacturer.code && game === bonus.game.slug
    })
  }

  const getBonusType = computed(() => {
    const filters = activeStep.value?.filters

    if (filters?.some((i) => i.type === FILTER_GAMES.ONLY_SPORT)) {
      return 'Sports'
    }

    return 'BonusGames'
  })
  const getConditionsChoices = computed(() => {
    return conditionsChoices.value ?? []
  })
  const countOfBonuses = computed(() => {
    let count = getProgramsAvailable.value?.length
    if (getActiveStep.value && getActiveStep.value?.condition !== 7) {
      count++
    }
    if (getFreeBets.value) {
      count++
    }
    if (freeSpins.value) {
      count = freeSpins.value?.length + count
    }

    return count
  })

  function updateProgramAvailable(payload) {
    programsAvailable.value = payload
    programsAvailableReady.value = true
  }

  function updateActiveStep(payload) {
    activeStep.value = payload
    activeStepLoaded.value = true
  }

  function updateActiveStepProgram(payload) {
    if (!activeStep.value) {
      return
    }
    activeStep.value.userProgram = payload
  }

  function updateActiveStepLoaded() {
    activeStepLoaded.value = true
  }

  function deleteUserProgram(payload) {
    if (getActiveUserProgram.value?.id) {
      if (+getActiveUserProgram.value.id === +payload) {
        bonusGameIdsList.value = null
        activeStep.value = null
      }
    }

    programsAvailable.value = programsAvailable.value.filter((el) => +el.id !== +payload)
  }

  function updateUserProgram(payload) {
    if (programsAvailable.value.some((el) => +el.id === +payload.id)) {
      programsAvailable.value = programsAvailable.value.map((program) => {
        return +program.id === +payload.id ? { ...program, ...payload } : program
      })
    } else {
      programsAvailable.value = [...programsAvailable.value, payload]
    }
  }

  function updateProgram(payload) {
    programsAvailable.value = programsAvailable.value.reduce((programsAvailable, userProgram) => {
      if (+userProgram?.program.id === +payload.id) {
        programsAvailable.push({ ...userProgram, program: { ...userProgram.program, ...payload } })
        return programsAvailable
      }
      programsAvailable.push(userProgram)
      return programsAvailable
    }, [])
  }

  function updateUserStepBalance(payload) {
    if (activeStep.value?.balance !== undefined && payload?.user_step_id === activeStep.value?.id) {
      activeStep.value = { ...activeStep.value, balance: payload.balance }
    }
  }

  function updateFreeBets(payload) {
    freeBets.value = payload
    freeBetsRequestReady.value = true
  }

  function updateCurrentLiveCashBack(payload) {
    currentLiveCashBack.value = payload
  }

  function removeFreespinByID(payload) {
    freeSpins.value = freeSpins.value.filter((el) => el.id !== payload)
  }

  function updatePromoFreespinAvailable(payload) {
    const freeSpinsLocal = [...payload]

    if (payload.length === 0 && freeSpins.value?.length !== 0) {
      freeSpinEnd.value = true
    }

    freeSpins.value = freeSpinsLocal.sort((a, b) => b.activated - a.activated)
    freeSpinsRequestReady.value = true
  }

  function updateConditionsChoices(payload) {
    conditionsChoices.value = payload
  }

  function updateBonusGameIdsList(payload) {
    bonusGameIdsList.value = payload
  }

  function updateFreespinEnd(payload) {
    freeSpinEnd.value = payload
  }

  return {
    // refs
    freeSpins,
    freeSpinsRequestReady,
    programsAvailableReady,
    freeBets,
    freeBetsRequestReady,
    programsAvailable,
    activeStep,
    activeStepLoaded,
    activeUserProgram,
    conditionsChoices,
    bonusGameIdsList,
    freeSpinEnd,
    currentLiveCashBack,
    // computed
    getActiveUserProgram,
    getBonusGameIdsList,
    getProgramsAvailable,
    getConditionsChoices,
    countOfBonuses,
    getActiveStep,
    getConditionStep,
    getBonusType,
    getFreeSpins,
    getFreeBets,
    getActiveStepLoaded,
    showBonusesGames,
    //functions
    updateProgramAvailable,
    updateActiveStepLoaded,
    updateActiveStep,
    updateActiveStepProgram,
    updateUserProgram,
    deleteUserProgram,
    updateProgram,
    updateUserStepBalance,
    updateFreeBets,
    updateCurrentLiveCashBack,
    removeFreespinByID,
    updatePromoFreespinAvailable,
    updateConditionsChoices,
    updateBonusGameIdsList,
    updateFreespinEnd,
    hasFreespinsOnGame,
  }
})
